import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router'

import { Icon } from '@/components/Icon'
import { fetchDivisions, fetchProjects } from '@/features/portfolio/api'
import type { ApiError } from '@/lib/api/client'
import type { Division, Project } from '@/types/domain'

/**
 * Divisions and the projects inside them, as a tree you can walk.
 *
 * This is the thing a work tool lives or dies by: getting to a board should be
 * one click from anywhere, not a trip through a picker. Divisions stand in for
 * spaces and projects for lists — the hierarchy the platform already has, rather
 * than a second one invented to match somebody else's product.
 *
 * Both queries are the ordinary scoped endpoints, so a guest sees the one
 * project they hold and no structure around it.
 */
export function SpacesTree() {
  const { t } = useTranslation()
  const [open, setOpen] = useState<Record<string, boolean>>({})

  const divisions = useQuery<Division[], ApiError>({
    queryKey: ['divisions'],
    queryFn: fetchDivisions,
    retry: false,
  })

  const projects = useQuery<Project[], ApiError>({
    queryKey: ['projects'],
    queryFn: () => fetchProjects(),
    retry: false,
  })

  if (divisions.data === undefined) return null

  const byDivision = new Map<string, Project[]>()
  for (const project of projects.data ?? []) {
    byDivision.set(project.divisionId, [...(byDivision.get(project.divisionId) ?? []), project])
  }

  return (
    <div className="mt-4">
      <div className="text-ink-500 flex items-center gap-1.5 px-4 pb-1.5 font-mono text-[10px] tracking-widest uppercase">
        {t('nav.spaces')}
      </div>

      <ul className="px-2">
        {divisions.data.map((division) => {
          const children = byDivision.get(division.id) ?? []
          const expanded = open[division.id] ?? false

          return (
            <li key={division.id}>
              <button
                type="button"
                onClick={() => setOpen((state) => ({ ...state, [division.id]: !expanded }))}
                aria-expanded={expanded}
                className="text-ink-700 hover:bg-surface-2 flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-start text-[13px]"
              >
                <Icon
                  name="chevron"
                  className={`size-3 transition-transform rtl:-scale-x-100 ${expanded ? 'rotate-90' : ''}`}
                />
                <span
                  className="size-2 shrink-0 rounded-[3px]"
                  style={{ backgroundColor: colorFor(division.key) }}
                />
                <span className="min-w-0 flex-1 truncate">{division.name}</span>
                <span className="text-ink-500 font-mono text-[10px] tabular-nums">
                  {division.projectCount}
                </span>
              </button>

              {expanded ? (
                <ul className="border-line ms-4 border-s ps-1.5">
                  {children.length === 0 ? (
                    <li className="text-ink-500 px-2 py-1 text-[11px]">{t('work.noItems')}</li>
                  ) : (
                    children.map((project) => (
                      <li key={project.id}>
                        <NavLink
                          to={`/projects/${project.key}`}
                          className={({ isActive }) =>
                            [
                              'flex items-center gap-1.5 rounded px-2 py-1 text-[12px]',
                              isActive
                                ? 'bg-brand-50 text-brand-700 font-medium'
                                : 'text-ink-700 hover:bg-surface-2',
                            ].join(' ')
                          }
                        >
                          <Icon name="list" className="size-3 opacity-60" />
                          <span className="min-w-0 flex-1 truncate">{project.name}</span>
                          <span className="text-ink-500 font-mono text-[9px]">{project.key}</span>
                        </NavLink>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Stable per division key, so a space keeps its colour without a column for it. */
function colorFor(key: string | null): string {
  if (key === null) return 'var(--color-ink-500)'

  let hash = 0
  for (let i = 0; i < key.length; i++) hash = (hash * 37 + key.charCodeAt(i)) % 360

  return `hsl(${hash} 58% 52%)`
}
